/**
 * Payment Method Controller
 *
 * A custom React hook that manages the Stripe payment flow for membership subscriptions.
 * This controller handles multiple states of the payment process:
 *
 * 1. Price calculation and display - Manages subtotal, discounts, taxes, and final amount
 * 2. Coupon validation and application - Validates coupon codes and applies discounts
 * 3. Waitlist/Invite flow - Handles users with invite codes or joining waitlist
 * 4. Payment submission - Processes payment through Stripe and handles responses
 * 5. Error handling - Provides user feedback for various error conditions
 *
 * The component integrates with Stripe Elements for secure payment collection
 * and calls membership APIs to validate pricing and create subscriptions.
 */

import { useMeControllerGetUserInfo } from '@/api/generated/default/default';
import {
  useMembershipControllerConfirmSubscriptionV1,
  useMembershipControllerConfirmTrialVerification,
  useMembershipControllerCreateSubscriptionV1,
  useMembershipControllerCreateTrialVerification,
  useMembershipControllerGetUserPrice,
} from '@/api/generated/membership/membership';
import { PaymentError } from '@/app/errors/payment-error';
import { PaymentFormState } from '@/app/components/ppra/components/ppra-payment-page/types';
import { ROUTE_DASHBOARD, ROUTE_MULTI_DISEASE_WELCOME, ROUTE_PAYMENT_VERIFY_PAYMENT, ROUTE_QUESTIONNAIRE_V2, ROUTE_SIGN_IN_ENTER_EMAIL } from '@/constants/routes';
import { TOAST_DEFAULT_OPTIONS } from '@/constants/toast';
import useAnalyticsCalls from '@/hooks/use-analytics';
import usePureState from '@/hooks/use-pure-state';
import useReceiveInviteCode from '@/hooks/use-receive-invite-code';
import useOnMount from '@/hooks/useOnMount';
import useVisitorSession from '@/hooks/useVisitorSession';
import authService from '@/services/auth.service';
import { useToast } from '@cmpsr/components';
import { useElements, useStripe } from '@stripe/react-stripe-js';
import { StripeError, StripeExpressCheckoutElementConfirmEvent } from '@stripe/stripe-js';
import { AxiosError } from 'axios';
import { useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { COHORT_STORAGE_KEY, HSA_FSA_DECLINE_CODES } from '@/constants/payment';
import { usePurchaseModel } from '@/hooks/use-purchase-model';

// Types for price data structure used throughout the component
export type PriceItem = {
  description: string;
  amount: number;
};

export type PriceDataType = {
  lines: Array<PriceItem>;
  summary: PriceItem;
};

// Helper function to build price data from individual components
export const buildPriceData = (
  subtotal: number | undefined,
  discount: number | undefined,
  tax: number | undefined,
  total: number | undefined
): PriceDataType => {
  const lines = [{ description: 'SUBTOTAL', amount: subtotal ?? 0 }];

  if (discount && discount > 0) lines.push({ description: 'DISCOUNT', amount: discount });
  if (tax && tax > 0) lines.push({ description: 'TAX', amount: tax });

  return { lines, summary: { description: 'TOTAL', amount: total ?? 0 } };
};

// State type for the payment method hook
type useCollectPaymentMethodState = {
  priceData: PriceDataType;
  validationError: string | false | null;
  analyticsDataSent: boolean;
  isCouponVisible: boolean;
  zip: string | null;
  coupon: string;
  tempCoupon: string;
  confirmedAccount: boolean;
  isEmailEditable: boolean;
  isSubmitting: boolean;
  paymentScreenAnalyticsSent: boolean;
  inviteCode: string | null;
};

/**
 * Checks whether a Stripe error indicates an HSA/FSA card decline.
 * Returns true when the error is a card decline with a benefits-related
 * decline code on a debit or prepaid funding type (HSA/FSA cards report
 * as "prepaid" in Stripe).
 */
export function isHsaFsaDecline(error: StripeError): boolean {
  const funding = error.payment_method?.card?.funding;
  return (
    error.type === 'card_error' &&
    error.decline_code != null &&
    HSA_FSA_DECLINE_CODES.has(error.decline_code) &&
    (funding === 'debit' || funding === 'prepaid')
  );
}

export default function useCollectPaymentMethodV2() {
  // Get cohort from sessionStorage (set by PaymentPageV2 from URL param)
  const getCohort = () => sessionStorage.getItem(COHORT_STORAGE_KEY) || undefined;

  // Core API mutations and hooks for payment processing
  const membershipMutation = useMembershipControllerCreateSubscriptionV1();
  const trialVerificationMutation = useMembershipControllerCreateTrialVerification();
  const confirmTrialVerificationMutation = useMembershipControllerConfirmTrialVerification();
  const confirmSubscriptionMutation = useMembershipControllerConfirmSubscriptionV1();
  const analytics = useAnalyticsCalls();
  const elements = useElements();
  const navigate = useNavigate();

  const stripe = useStripe();
  const toast = useToast();

  /** Show a persistent warning toast for HSA/FSA card declines. */
  const showHsaFsaDeclineToast = () => {
    toast.close('hsa-fsa-decline');
    toast({
      ...TOAST_DEFAULT_OPTIONS,
      id: 'hsa-fsa-decline',
      duration: null,
      isClosable: true,
      title: 'Your card was declined',
      description:
        'Some debit cards, including many HSA and FSA cards, are restricted to certain merchant categories and may not work here. Try a personal card instead and we\u2019ll send you an itemized receipt you can submit to your plan administrator.',
      status: 'warning',
    });
  };

  const [params] = useSearchParams();
  const errorToShow = params.get('error');
  const { isTrial, purchaseModel } = usePurchaseModel();
  const { initializeSession, email, captureLead } = useVisitorSession();
  const { inviteCode: inviteCodeFromReceiveInviteCode } = useReceiveInviteCode();
  const { data: memberData } = useMeControllerGetUserInfo();

  useEffect(() => {
    // Redirect users with an active/trialing subscription away from payment collection
    const membershipStatus = memberData?.membership?.status;
    if (membershipStatus === 'active' || membershipStatus === 'trialing') {
      navigate(ROUTE_DASHBOARD);
    }
  }, [memberData]);

  // Initialize component state with default values
  const { state, setPartialState } = usePureState<useCollectPaymentMethodState>({
    zip: '',
    coupon: '',
    tempCoupon: '',
    priceData: buildPriceData(0, 0, 0, 0),
    validationError: null,
    analyticsDataSent: false,
    isCouponVisible: false,
    confirmedAccount: false,
    // Determines if waitlist message is shown. If invite code is provided, we assume the user has been cleared from waitlist.
    isEmailEditable: true,
    isSubmitting: false,
    paymentScreenAnalyticsSent: false,
    inviteCode: inviteCodeFromReceiveInviteCode,
  });

  const {
    zip,
    coupon,
    tempCoupon,
    priceData,
    validationError,
    analyticsDataSent,
    isCouponVisible,
    isEmailEditable,
    isSubmitting,
    paymentScreenAnalyticsSent,
    inviteCode,
  } = state;

  // Fetch pricing data based on current coupon and zip
  const { data: getPriceData, mutateAsync: refetch, isPending } = useMembershipControllerGetUserPrice();

  // Helper functions for coupon handling
  const setCoupon = (newCoupon: string) => {
    setPartialState({ tempCoupon: newCoupon, validationError: false });
    form.setValue('coupon', newCoupon);
  };
  const showCoupon = () => setPartialState({ isCouponVisible: true, validationError: false });
  const setIsEmailEditable = (isEmailEditable: boolean) => setPartialState({ isEmailEditable });

  // Reset validation error when coupon changes
  useEffect(() => setPartialState({ validationError: null }), [coupon, setPartialState]);

  // Track analytics revenue when price data is available
  useEffect(() => {
    if (!priceData || analyticsDataSent || priceData.summary.amount <= 0) return;
    setPartialState({ analyticsDataSent: true });
    analytics.callRevenue(priceData.summary.amount / 100, 'draft');
  }, [priceData?.summary.amount]);

  // Initialize form with default values
  const defaultValues = {
    email: email ?? '',
    name: '',
    legalAge: false,
    autoRenewal: true,
    coupon: getPriceData?.defaultCoupon ?? '',
    zip: '',
    inviteCode: inviteCode ?? undefined,
  };
  const form = useForm<PaymentFormState>({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues,
  });

  // Updates the coupon value in the form
  const updateCouponFormField = (coupon: string) => {
    if (!form.getValues('coupon')) form.register('coupon');
    form.setValue('coupon', coupon ?? '');
  };

  /**
   * Fetches updated pricing information based on the current coupon and postal code values.
   * Updates the price data state, validates the coupon, and updates the Stripe elements
   * with the new total amount if elements are available.
   */
  const getPrice = async (couponOverride?: string) => {
    const couponToUse = couponOverride !== undefined ? couponOverride : tempCoupon;
    await setPartialState({ zip: form.getValues('zip'), coupon: couponToUse });
    const response = await refetch({
      data: {
        inviteCode: inviteCode ?? undefined,
        coupon: couponToUse,
        zipCode: form.getValues('zip') || undefined,
        isTrial,
        cohort: getCohort(),

      },
    });
    if (!response) {
      console.error('Failed to fetch price data');
      return;
    }

    // Coupon is valid if:
    // 1. It has a discount applied (discount > 0), OR
    // 2. It's recognized by the system (defaultCoupon is set) - for one-time coupons on trials
    const isCouponRecognized = !!response.defaultCoupon;
    const hasDiscount = response.discount && response.discount > 0;
    const isCouponValid = !couponToUse || hasDiscount || isCouponRecognized;

    setPartialState({
      priceData: buildPriceData(response.subtotal, response.discount, response.tax, response.total),
      validationError: !isCouponValid ? 'Invalid coupon' : false,
    });

    // Dispatch event to update parent Elements container and update elements
    if (response.total > 0) {
      window.dispatchEvent(new CustomEvent('stripe-amount-update', {
        detail: { amount: response.total }
      }));

      if (elements)
        elements.update({
          amount: response.total,
          currency: 'usd',
        });
    }
  };

  // Initial price data setup when data is first received
  // Track if price data has been initialized to avoid infinite loops for $0 trials
  const hasPriceDataInitialized = useRef(false);
  useEffect(() => {
    if (getPriceData?.subtotal && !hasPriceDataInitialized.current) {
      hasPriceDataInitialized.current = true;
      const newCoupon = getPriceData?.defaultCoupon ?? tempCoupon;

       setPartialState({
         priceData: buildPriceData(getPriceData?.subtotal, getPriceData?.discount, getPriceData?.tax, getPriceData?.total),
         tempCoupon: newCoupon,
         isCouponVisible: !!newCoupon,
       });

       // Update the form field with the coupon from the API response
       updateCouponFormField(newCoupon);

      // Dispatch event to update parent Elements container
      if (getPriceData.total > 0) {
        window.dispatchEvent(new CustomEvent('stripe-amount-update', {
          detail: { amount: getPriceData.total }
        }));

        if (elements)
          elements.update({
            amount: getPriceData.total,
            currency: 'usd',
          });
      }

      if (!paymentScreenAnalyticsSent) {
        analytics.callPaymentScreenViewed(
          (getPriceData?.subtotal ?? 0) / 100,
          (getPriceData?.discount ?? 0) / 100,
          (getPriceData?.total ?? 0) / 100
        );
        setPartialState({ paymentScreenAnalyticsSent: true });
      }
    }
  }, [getPriceData?.subtotal, getPriceData?.discount, getPriceData?.tax, getPriceData?.total, getPriceData?.defaultCoupon, tempCoupon, paymentScreenAnalyticsSent, analytics, setPartialState]);

  // Function to join waitlist with email and invite code
  const joinWaitlist = async () => {
    try {
      const email = form.getValues('email');
      const inviteCode = form.getValues('inviteCode');
      if (!email) {
        return;
      }
      const leadResponse = await captureLead(email, inviteCode);
      setPartialState({ confirmedAccount: true });
      setPartialState({ inviteCode: leadResponse.inviteCode });
      if (leadResponse.inviteCode) {
        form.clearErrors('inviteCode');
      }
    } catch (error) {
      if (error instanceof AxiosError && error.response?.status === 404) {
        toast({
          title: 'Error',
          description: 'Invalid invite code',
          status: 'error',
        });
      }
    }
  };

  /**
   * Handle trial subscription with $1 authorization hold verification.
   * This verifies the card has available funds before creating the trial.
   */
  const handleTrialSubmit = async (values: PaymentFormState, leadResponse: { userId: string }) => {
    if (!elements || !stripe) return;

    try {
      analytics.callIdentifyEmail(leadResponse.userId, values.email);
    } catch (e) {
      console.warn('Analytics error', e);
    }

    // Step 1: Create verification PaymentIntent ($1 authorization hold)
    const { verificationClientSecret, verificationPaymentIntentId } =
      await trialVerificationMutation.mutateAsync({
        data: {
          email: values.email,
          couponName: values.coupon || undefined,
          zipCode: values.zip || undefined,
          cohort: getCohort(),
  
        },
      });

    // Step 2: Confirm the $1 PaymentIntent to authorize the card
    const { error: confirmError } = await stripe.confirmPayment({
      clientSecret: verificationClientSecret,
      elements,
      confirmParams: {
        payment_method_data: {
          billing_details: {
            name: values.name || undefined,
            email: values.email || undefined,
            address: { country: 'US', postal_code: values.zip },
          },
        },
      },
      redirect: 'if_required', // Don't redirect, handle inline
    });

    if (confirmError) {
      if (isHsaFsaDecline(confirmError)) {
        showHsaFsaDeclineToast();
        return;
      }
      throw new PaymentError(confirmError.message ?? 'Card verification failed. Please try a different card.');
    }

    // Step 3: Confirm trial verification (cancels the $1 hold and creates trial subscription)
    const { subscriptionId } = await confirmTrialVerificationMutation.mutateAsync({
      data: {
        verificationPaymentIntentId,
        couponName: values.coupon || undefined,
        zipCode: values.zip || undefined,
        autoRenewal: true,
        cohort: getCohort(),

      },
    });

    // Step 4: Confirm subscription to get auth token and log in the user
    const authResponse = await confirmSubscriptionMutation.mutateAsync({
      data: { subscriptionId, name: values.name || undefined },
    });
    authService.login(authResponse);
    analytics.callIdentifyMembership(authResponse.userId, values.email, 'member');

    // Step 5: Navigate to questionnaire (or multi-disease welcome for cohort users)
    const destination = getCohort() === 'multi-disease' ? ROUTE_MULTI_DISEASE_WELCOME : ROUTE_QUESTIONNAIRE_V2;
    navigate(destination, { replace: true });
  };

  /**
   * Handle regular (non-trial) subscription payment flow.
   */
  const handleRegularSubmit = async (values: PaymentFormState, leadResponse: { userId: string }) => {
    if (!elements || !stripe) return;

    // Create membership/subscription
    const { type, clientSecret, subscriptionId } = await membershipMutation.mutateAsync({
      data: {
        email: values.email,
        couponName: values.coupon,
        zipCode: values.zip,
        autoRenewal: true,
        isTrial: false,
        cohort: getCohort(),

      },
    });

    // Determine whether to confirm a payment or setup intent based on type
    const confirmIntent = type === 'payment' ? stripe.confirmPayment : stripe.confirmSetup;

    try {
      analytics.callIdentifyEmail(leadResponse.userId, values.email);
    } catch (e) {
      console.warn('Analytics error', e);
    }

    // Confirm payment with Stripe and handle redirect
    const returnUrl = new URL(`${window.location.origin.toString()}${ROUTE_PAYMENT_VERIFY_PAYMENT}`);
    returnUrl.searchParams.set('subscriptionId', subscriptionId);

    const { error } = await confirmIntent({
      clientSecret,
      elements,
      confirmParams: {
        return_url: returnUrl.toString(),
        payment_method_data: {
          billing_details: {
            name: values.name || undefined,
            email: values.email || undefined,
            address: { country: 'US', postal_code: values.zip },
          },
        },
      },
    });
    if (error) {
      if (isHsaFsaDecline(error)) {
        showHsaFsaDeclineToast();
        return;
      }
      throw new PaymentError(error.message ?? 'An error occurred processing payment');
    }

    // Important: confirmIntent above will redirect to verify payment page
  };

  // Main form submission handler that processes payment
  const handleSubmit = form.handleSubmit(async (values) => {
    if (!elements || !stripe) return;

    try {
      setPartialState({ isSubmitting: true });
      // Create lead using the mutation
      const leadResponse = await captureLead(values.email, inviteCode ?? undefined);

      // Ensure we have a userId from the lead response
      if (!leadResponse?.userId) {
        throw new Error('Failed to create lead: No userId returned');
      }

      // Submit payment form to Stripe for validation
      const { error: submitError } = await elements.submit();
      if (submitError) {
        toast({
          ...TOAST_DEFAULT_OPTIONS,
          title: 'Payment Error',
          description: submitError.message || 'Please check your payment information and try again.',
          status: 'error',
        });
        return;
      }

      try {
        // Use different flow for trial vs regular subscriptions
        if (isTrial) {
          await handleTrialSubmit(values, leadResponse);
        } else {
          await handleRegularSubmit(values, leadResponse);
        }
      } catch (error) {
        if (
          error instanceof AxiosError &&
          error?.response?.data?.title === 'User already subscribed'
        ) {
          // Handle already subscribed error
          toast({
            ...TOAST_DEFAULT_OPTIONS,
            title: 'Already Subscribed',
            description: 'You already have an active subscription. Redirecting to sign in...',
            status: 'info',
            duration: 10000
          });

          // Redirect to sign in with email pre-filled after a delay
          setTimeout(() => {
            const email = values.email;
            navigate(`${ROUTE_SIGN_IN_ENTER_EMAIL}?email=${encodeURIComponent(email)}`);
          }, 2000);
        } else {
          // Handle general payment errors with appropriate messages
          const defaultErrorMessage = 'We encountered an issue processing your payment. Please try again later.';
          const errorMessage =
            error instanceof AxiosError
              ? error?.response?.data?.message ?? error?.response?.data?.detail ?? defaultErrorMessage
              : error instanceof Error
              ? error?.message ?? defaultErrorMessage
              : defaultErrorMessage;

          toast({
            ...TOAST_DEFAULT_OPTIONS,
            title: 'Payment Error',
            description: errorMessage,
            status: 'error',
          });
        }
      }
    } catch (error) {
      // Handle general payment errors with appropriate messages
      const defaultErrorMessage = 'We encountered an issue processing your payment. Please try again later.';

      toast({
        ...TOAST_DEFAULT_OPTIONS,
        title: 'Payment Error',
        description: defaultErrorMessage,
        status: 'error',
      });
    } finally {
      setPartialState({ isSubmitting: false });
    }
  });

  // Display error from URL parameters if present
  useEffect(() => {
    if (errorToShow) {
      toast({
        ...TOAST_DEFAULT_OPTIONS,
        title: 'Error',
        description: errorToShow,
        status: 'error',
      });
    }
  }, [errorToShow]); // eslint-disable-line react-hooks/exhaustive-deps

  // Track whether we've made the initial price fetch with the experiment value
  const hasInitialPriceFetchRef = useRef(false);

  useOnMount(() => {
    async function initialize() {
      // Initialize session
      const response = await initializeSession();
      if (response) {
        if (response.email) {
          setPartialState({ isEmailEditable: false });
          form.setValue('email', response.email);
        }
      }

      hasInitialPriceFetchRef.current = true;
      getPrice();
    }
    initialize();
  });

  // Get the current price from the priceData if available
  const currentPrice = priceData?.lines?.[0]?.amount ? priceData.lines[0].amount : null;

  /**
   * Handle Express Checkout payment confirmation
   * This is called when a user clicks on Apple Pay, Google Pay, etc.
   * @param event - The Express Checkout confirmation event containing billing details
   */
  const handleExpressCheckout = async (event: StripeExpressCheckoutElementConfirmEvent) => {
    if (!stripe || !elements) {
      // Setup error - just return, payment sheet closes automatically
      return;
    }

    // Declare checkoutEmail outside try block so it's accessible in catch block
    let checkoutEmail: string | undefined;
    // Track whether the error came from stripe.confirmPayment() - if so, Stripe already
    // handled the failure in the payment sheet, so we should NOT call paymentFailed() again
    let isStripePaymentError = false;

    try {
      setPartialState({ isSubmitting: true });

      // Use form email, fallback to wallet email from Express Checkout
      checkoutEmail = form.getValues('email') || event.billingDetails?.email || '';

      // Use form name, fallback to wallet name from Express Checkout
      const checkoutName = form.getValues('name') || event.billingDetails?.name || '';

      const zip = event.billingDetails?.address?.postal_code ||
                  (event as any).shippingAddress?.postalCode ||
                  form.getValues('zip') || '';

      // Validate email exists (either from form or wallet)
      if (!checkoutEmail) {
        // Validation error before any Stripe API call - just return
        // Payment sheet closes, user sees toast and can fix email
        toast({
          ...TOAST_DEFAULT_OPTIONS,
          title: 'Email Required',
          description: 'You must provide an email address.',
          status: 'error',
        });
        return;
      }

      // Create lead
      const leadResponse = await captureLead(checkoutEmail, inviteCode ?? undefined);

      if (!leadResponse?.userId) {
        throw new Error('Failed to create lead: No userId returned');
      }

      // Submit the elements to get the payment method
      const { error: submitError } = await elements.submit();
      if (submitError) {
        throw new PaymentError(submitError.message || 'Failed to submit payment details');
      }

      try {
        analytics.callIdentifyEmail(leadResponse.userId, checkoutEmail);
      } catch (e) {
        console.warn('Analytics error', e);
      }

      // Use different flow for trial vs regular subscriptions
      if (isTrial) {
        // Trial flow: Use $1 authorization hold verification
        // Step 1: Create verification PaymentIntent ($1 authorization hold)
        const { verificationClientSecret, verificationPaymentIntentId } =
          await trialVerificationMutation.mutateAsync({
            data: {
              email: checkoutEmail,
              couponName: form.getValues('coupon') || tempCoupon || undefined,
              zipCode: zip || undefined,
              cohort: getCohort(),
      
            },
          });

        // Step 2: Confirm the $1 PaymentIntent to authorize the card
        const { error: confirmError } = await stripe.confirmPayment({
          clientSecret: verificationClientSecret,
          elements,
          confirmParams: {
            payment_method_data: {
              billing_details: {
                name: checkoutName || undefined,
                email: checkoutEmail || undefined,
                address: { country: 'US', postal_code: zip },
              },
            },
          },
          redirect: 'if_required', // Don't redirect, handle inline
        });

        if (confirmError) {
          isStripePaymentError = true;
          if (isHsaFsaDecline(confirmError)) {
            showHsaFsaDeclineToast();
            return;
          }
          throw new PaymentError(confirmError.message ?? 'Card verification failed. Please try a different card.');
        }

        // Step 3: Confirm trial verification (cancels the $1 hold and creates trial subscription)
        const { subscriptionId } = await confirmTrialVerificationMutation.mutateAsync({
          data: {
            verificationPaymentIntentId,
            couponName: form.getValues('coupon') || tempCoupon || undefined,
            zipCode: zip || undefined,
            autoRenewal: true,
            cohort: getCohort(),
    
          },
        });

        // Step 4: Confirm subscription to get auth token and log in the user
        const authResponse = await confirmSubscriptionMutation.mutateAsync({
          data: { subscriptionId, name: checkoutName || undefined },
        });
        authService.login(authResponse);
        analytics.callIdentifyMembership(authResponse.userId, checkoutEmail, 'member');

        // Step 5: Navigate to questionnaire (or multi-disease welcome for cohort users)
        const destination = getCohort() === 'multi-disease' ? ROUTE_MULTI_DISEASE_WELCOME : ROUTE_QUESTIONNAIRE_V2;
        navigate(destination, { replace: true });
      } else {
        // Regular flow: Create subscription and confirm payment
        const { type, clientSecret, subscriptionId } = await membershipMutation.mutateAsync({
          data: {
            email: checkoutEmail,
            couponName: form.getValues('coupon') || tempCoupon,
            zipCode: zip,
            autoRenewal: true,
            isTrial: false,
            cohort: getCohort(),
    
          },
        });

        // Determine the confirmation method based on type
        const confirmMethod = type === 'payment' ? stripe.confirmPayment : stripe.confirmSetup;

        // Confirm the payment/setup with redirect
        const { error: confirmError } = await confirmMethod({
          clientSecret,
          elements,
          confirmParams: {
            return_url: `${window.location.origin.toString()}${ROUTE_PAYMENT_VERIFY_PAYMENT}?subscriptionId=${subscriptionId}`,
            payment_method_data: {
              billing_details: {
                name: checkoutName || undefined,
                email: checkoutEmail || undefined,
                address: { country: 'US', postal_code: zip },
              },
            },
          },
          redirect: 'always',
        });

        if (confirmError) {
          isStripePaymentError = true;
          if (isHsaFsaDecline(confirmError)) {
            showHsaFsaDeclineToast();
            return;
          }
          throw new PaymentError(confirmError.message || 'Payment confirmation failed');
        }
      }
    } catch (error) {
      // For Express Checkout, we should NOT call paymentFailed() in most cases:
      // 1. Validation errors (missing email, etc.) - sheet closes automatically when handler returns
      // 2. API errors before payment - sheet closes automatically
      // 3. Stripe payment errors - Stripe already showed the error in the payment sheet
      //
      // Calling paymentFailed() when Stripe doesn't expect it causes:
      // "IntegrationError: Unexpected call to paymentFailed()"
      //
      // The only case where we might need paymentFailed() is for errors AFTER stripe.confirmPayment()
      // succeeds but before we finish (e.g., confirmTrialVerification fails). However, even then
      // it's safer to just show a toast since the payment sheet state is uncertain.
      // For Stripe payment errors, don't show toast - the error is already displayed
      // in the payment sheet by Stripe
      if (isStripePaymentError) {
        return;
      }

      // For non-Stripe errors, show toast to inform the user
      let errorMessage = 'Payment failed. Please try again.';
      let errorTitle = 'Payment Error';

      // Check if it's an Axios error with response data
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as AxiosError<{ title?: string; detail?: string }>;

        if (axiosError.response?.data?.title === 'User already subscribed') {
          errorTitle = 'Already Subscribed';
          errorMessage = 'You already have an active subscription. Redirecting to sign in...';

          // Redirect to sign in with email pre-filled after a delay
          // Use session email from form
          const redirectEmail = form.getValues('email') || '';
          setTimeout(() => {
            navigate(`${ROUTE_SIGN_IN_ENTER_EMAIL}?email=${encodeURIComponent(redirectEmail)}`);
          }, 2000);
        } else if (axiosError.response?.data?.detail) {
          errorMessage = axiosError.response.data.detail;
        }
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }

      toast({
        ...TOAST_DEFAULT_OPTIONS,
        title: errorTitle,
        description: errorMessage,
        status: 'error',
      });
    } finally {
      setPartialState({ isSubmitting: false });
    }
  };

  // Export public API of the hook
  return {
    email,
    form,
    handleSubmit,
    handleExpressCheckout,
    couponValidationError: validationError,
    getPrice,
    joinWaitlist,
    coupon: tempCoupon,
    setCoupon,
    priceData,
    isCouponVisible,
    showCoupon,
    isSubmitting: isSubmitting || isPending,
    isEmailEditable,
    setIsEmailEditable,
    inviteCode,
    currentPrice,
    priceRecurring: getPriceData?.recurring,
    priceSubtotal: getPriceData?.subtotal,
    isTrial,
    purchaseModel,
    isPriceLoading: isPending,
  };
}
